import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'react-feather';
import { authAPI } from '../api';
import BrandWordmark from '../components/BrandWordmark';
import './Auth.css';

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token') || '';
  const hasResetToken = Boolean(resetToken);

  const [methods, setMethods] = useState({ email: true, code: true });
  const [email, setEmail] = useState('');
  const [resetForm, setResetForm] = useState({ code: '', newPassword: '', confirmPassword: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLinkHint, setResetLinkHint] = useState('');
  const [visibleResetCode, setVisibleResetCode] = useState('');
  const [requestComplete, setRequestComplete] = useState(hasResetToken);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const step = hasResetToken || (requestComplete && methods.code) ? 'reset' : 'email';

  const resetHint = useMemo(() => {
    if (!resetLinkHint) return '';
    try {
      const parsed = new URL(resetLinkHint);
      return `${parsed.pathname}${parsed.search}`;
    } catch {
      return resetLinkHint;
    }
  }, [resetLinkHint]);

  useEffect(() => {
    authAPI.publicSettings()
      .then((response) => {
        setMethods({
          email: response.data?.settings?.reset_password_email_enabled !== '0',
          code: response.data?.settings?.reset_password_code_enabled !== '0',
        });
      })
      .catch(() => {
        setMethods({ email: true, code: true });
      });
  }, []);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await authAPI.forgotPassword(email);
      const initiatedMethods = response.data?.methods || [];
      const returnedCode = response.data?.resetCode || '';
      setMethods({
        email: initiatedMethods.includes('email') || (!initiatedMethods.length && methods.email),
        code: initiatedMethods.includes('code') || (!initiatedMethods.length && methods.code),
      });

      const parts = [];
      if (initiatedMethods.includes('email')) parts.push('a reset link has been sent to your email');
      if (initiatedMethods.includes('code')) parts.push('a verification code is ready for password reset');

      setMessage(parts.length ? `If an account exists with this email, ${parts.join(' and ')}.` : (response.data?.message || 'If an account exists with this email, reset instructions have been prepared.'));
      setResetLinkHint(response.data?.resetUrl || '');
      setVisibleResetCode(returnedCode);
      setResetForm((prev) => ({ ...prev, code: returnedCode || prev.code }));
      setRequestComplete(Boolean(returnedCode) || hasResetToken);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to start password reset. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!resetForm.newPassword || !resetForm.confirmPassword) {
      setError('Both password fields are required');
      return;
    }

    if (resetForm.newPassword !== resetForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (resetForm.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!hasResetToken && methods.code && !resetForm.code) {
      setError('Verification code is required');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword({
        token: resetToken,
        email: hasResetToken ? undefined : email,
        code: hasResetToken ? undefined : resetForm.code,
        newPassword: resetForm.newPassword,
      });
      setMessage('Password has been reset successfully! Redirecting to login...');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-bg-grid" />
      <div className="auth-card">
        <div className="auth-logo">
          <BrandWordmark className="auth-brand-mark" />
        </div>
        <h1 className="auth-title">Reset password</h1>
        <p className="auth-sub">
          {step === 'email'
            ? 'Enter your email address and we will start the reset process'
            : hasResetToken
              ? 'Create a new password for your account'
              : 'Enter your verification code and choose a new password'}
        </p>

        {error && (
          <div className="alert alert-error">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertCircle size={18} style={{ flexShrink: 0, color: '#fca5a5' }} />
              <span>{error}</span>
            </div>
          </div>
        )}

        {message && (
          <div className="alert alert-success">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <CheckCircle size={18} style={{ flexShrink: 0, color: '#86efac' }} />
              <span>{message}</span>
            </div>
          </div>
        )}

        {step === 'email' ? (
          <form onSubmit={handleRequestReset}>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                className="form-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                disabled={loading}
                required
              />
            </div>
            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%', marginTop: 8 }}
              disabled={loading}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                  Sending...
                </span>
              ) : 'Continue'}
            </button>

            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.6 }}>
              {methods.email && methods.code && 'Users can reset by email link or verification code.'}
              {methods.email && !methods.code && 'Users can reset by email link only.'}
              {!methods.email && methods.code && 'Users can reset by verification code only.'}
            </div>

            {resetLinkHint && (
              <div className="alert alert-success" style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <CheckCircle size={18} style={{ flexShrink: 0, color: '#86efac' }} />
                  <span>Reset link prepared at: {resetHint}</span>
                </div>
              </div>
            )}

            {visibleResetCode && (
              <div className="alert alert-success" style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <CheckCircle size={18} style={{ flexShrink: 0, color: '#86efac' }} />
                  <span>Your verification code is: <strong>{visibleResetCode}</strong></span>
                </div>
              </div>
            )}
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            {!hasResetToken && methods.code && (
              <div className="form-group">
                <label className="form-label">Verification Code</label>
                <input
                  className="form-input"
                  type="text"
                  value={resetForm.code}
                  onChange={(e) => setResetForm({ ...resetForm, code: e.target.value })}
                  placeholder="Enter the 6-digit code"
                  autoComplete="one-time-code"
                  disabled={loading}
                  required={methods.code}
                />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">New Password</label>
              <div className="auth-password-field">
                <input
                  className="form-input"
                  type={showNewPassword ? 'text' : 'password'}
                  value={resetForm.newPassword}
                  onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })}
                  placeholder="min 8 characters"
                  autoComplete="new-password"
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowNewPassword((value) => !value)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <div className="auth-password-field">
                <input
                  className="form-input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={resetForm.confirmPassword}
                  onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })}
                  placeholder="repeat your password"
                  autoComplete="new-password"
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword((value) => !value)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%', marginTop: 8 }}
              disabled={loading}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                  Resetting...
                </span>
              ) : 'Reset Password'}
            </button>
          </form>
        )}

        <div className="auth-divider"><span>Remember your password?</span></div>

        <Link to="/login" className="btn btn-secondary btn-lg" style={{ width: '100%', justifyContent: 'center', textDecoration: 'none' }}>
          Back to Login
        </Link>

        <div className="auth-terms">
          Don't have an account? <Link to="/register" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Sign up here</Link>
        </div>
      </div>
    </div>
  );
}
